/**
 * Who actually pays for a ledger dollar: cash, or a credit grant.
 *
 * This is the ONE place the CRM decides funding. The Echo cost card and the
 * Costs page both read it, so a change in who pays (a credit grant ending, a
 * lane moving between Vertex and AI Studio) is a one-line edit here.
 *
 * Inputs follow the ledger contract of 2026-10-01
 * (plans/2026-10-01-gemini-38-flash-migration-plan.md, "Ledger / CRM contract"):
 *
 * - producers stamp `usage_detail.billing_channel` where a provider has two
 *   bills (Gemini: `google_vertex` vs `google_ai_studio`); the orchestrator
 *   stamps every LLM row;
 * - the indexer writes `echo_cost_by_billing_channel` (channel → USD) on the
 *   session PROFILE;
 * - rows without the field are derived from the provider EXACTLY as the
 *   indexer does (`echo_cost_billing_channel` in services/dashboard-indexer),
 *   so the CRM's line-item view and the session summary always agree.
 *
 * Funding rules:
 * - `google_vertex` → Google Cloud credits. Vertex AI in project
 *   `peppy-primacy-471814-p1` is paid from the GCP credit grant (Veo has
 *   always run there).
 * - `elevenlabs` → ElevenLabs credits (stated by the CTO, 2026-09-29 meeting).
 * - `elevenlabs_grant` → ElevenLabs credits too. The ECL vendor usage rate
 *   card (plans/evidence/2026-10-20-ecl/contracts.md, C2 `funding`) stamps it
 *   on ElevenLabs rows until the Startup Grant year ends on 2026-11-26.
 * - `google_unspecified` → cash, flagged as an unknown Google channel. The
 *   orchestrator's `google` rows from before 2026-10-01 do not say whether
 *   they billed Vertex or AI Studio. Calling them cash is the conservative
 *   reading: it never overstates what credits cover.
 * - `runpod` → cash. Rented RunPod GPU time for self-hosted H3 clips, priced
 *   from each clip's measured GPU seconds (ledger contract of 2026-10-02).
 * - `owned_gpu` → owned hardware. GPU time on our own RTX 5090 towers: the
 *   dollars are electricity plus depreciation of machines we already own, so
 *   they are neither cash nor credits and get their own bucket.
 * - `meta_whatsapp` → cash. Meta's per-message WhatsApp fees, billed in EUR on
 *   the WABA's payment method and booked in USD at the ECB rate of the day
 *   (EMC contract C3.4). Allocated WhatsApp marketing spend uses it too.
 * - `meta_ads` → cash. Allocated Meta ads spend (EMC contract C4.3, admin
 *   only on the Echo page).
 * - everything else (OpenAI, Anthropic, DeepSeek, Kling, Plainly, the AI Studio
 *   wallet, other, any channel name this file does not know yet) → cash.
 *
 * `self_hosted` is the legacy channel of self-hosted rows that carry no GPU
 * price (H3 clips without a worker receipt, rows from before 2026-10-02 that
 * the backfill could not price). It has no dollars to fund; the provider
 * derivation for `h3_comfy` and `ltx25_comfy` (self-hosted LTX-2.5, the
 * real-estate lane) still lands there.
 *
 * AWS is outside the per-Echo ledger altogether (credits currently absorb it;
 * see the AWS cards on the Costs page). RunPod shared worker overhead has its own allocation channel and is included
 * in per-Echo totals when native billing and usage evidence qualify.
 */

export const BILLING_CHANNELS = [
  "google_vertex",
  "google_ai_studio",
  "openai",
  "anthropic",
  "deepseek",
  "elevenlabs",
  "elevenlabs_grant",
  "kling",
  "plainly",
  "google_unspecified",
  "runpod",
  "runpod_overhead",
  "google_cloud_overhead",
  "vercel_overhead",
  "owned_gpu",
  "self_hosted",
  "meta_whatsapp",
  "meta_ads",
  "other",
] as const;

export type BillingChannel = (typeof BILLING_CHANNELS)[number];

const CHANNEL_SET: ReadonlySet<string> = new Set(BILLING_CHANNELS);

export const CHANNEL_LABELS: Record<BillingChannel, string> = {
  google_vertex: "Vertex AI",
  google_ai_studio: "Google AI Studio",
  openai: "OpenAI",
  anthropic: "Anthropic",
  deepseek: "DeepSeek",
  elevenlabs: "ElevenLabs",
  elevenlabs_grant: "ElevenLabs (grant)",
  kling: "Kling",
  plainly: "Plainly",
  google_unspecified: "Google (channel not recorded)",
  runpod: "RunPod (rented GPU)",
  runpod_overhead: "RunPod shared overhead (allocated)",
  google_cloud_overhead: "Google Cloud shared overhead (allocated)",
  vercel_overhead: "Vercel shared overhead (allocated)",
  owned_gpu: "Owned GPU towers",
  self_hosted: "Self-hosted GPU",
  meta_whatsapp: "Meta WhatsApp",
  meta_ads: "Meta ads",
  other: "Other",
};

export function isBillingChannel(value: unknown): value is BillingChannel {
  return typeof value === "string" && CHANNEL_SET.has(value);
}

/** Display name for any channel string, known or not. */
export function channelLabel(channel: string): string {
  return isBillingChannel(channel) ? CHANNEL_LABELS[channel] : channel;
}

/**
 * Providers whose rows predate `usage_detail.billing_channel` but have exactly
 * one possible bill — a copy of the indexer's `_BILLING_CHANNEL_BY_PROVIDER`.
 * The composer's `gemini` rows used the AI Studio key (its only Gemini client
 * until 2026-10-01); the orchestrator's `google` rows are ambiguous. A priced
 * H3 clip names `runpod` or `owned_gpu` on the row itself; without that it is
 * an unpriced `self_hosted` row, whatever machine rendered it.
 */
const CHANNEL_BY_PROVIDER: Record<string, BillingChannel> = {
  openai: "openai",
  anthropic: "anthropic",
  deepseek: "deepseek",
  elevenlabs: "elevenlabs",
  plainly: "plainly",
  gemini: "google_ai_studio",
  google: "google_unspecified",
  h3_comfy: "self_hosted",
  // Self-hosted LTX-2.5, the real-estate i2v lane from 2026-10-04 (contracts
  // §5). Same rule as H3: a priced clip names `runpod` / `owned_gpu` itself.
  ltx25_comfy: "self_hosted",
  qwen_selfhost: "self_hosted",
  comfy_image_fleet: "self_hosted",
  // EMC contract C3.7: the indexer's `_BILLING_CHANNEL_BY_PROVIDER` adds both.
  meta_whatsapp: "meta_whatsapp",
  meta_ads: "meta_ads",
};

/**
 * Channel for a row that does not name one, mirroring the indexer:
 * veo_gemini* → google_vertex, kling* → kling, then CHANNEL_BY_PROVIDER,
 * anything else → other.
 */
export function channelForProvider(provider?: string | null): BillingChannel {
  const id = (provider ?? "").trim().toLowerCase();
  if (id.startsWith("veo_gemini")) return "google_vertex";
  if (id.startsWith("kling")) return "kling";
  return CHANNEL_BY_PROVIDER[id] ?? "other";
}

/** The minimal line-item shape this module needs. */
export type ChannelledCost = {
  provider_id?: string | null;
  concrete_provider_id?: string | null;
  usage_detail?: Record<string, string> | null;
};

/**
 * The row's own channel when it records one — whatever the string, as the
 * indexer keeps it — else the provider derivation.
 */
export function billingChannelOf(cost: ChannelledCost): string {
  const recorded = cost.usage_detail?.billing_channel?.trim();
  if (recorded) return recorded;
  return channelForProvider(cost.concrete_provider_id || cost.provider_id);
}

/** True when the channel came from the row itself, not from a derivation. */
export function hasRecordedChannel(cost: ChannelledCost): boolean {
  return Boolean(cost.usage_detail?.billing_channel?.trim());
}

export type FundingKey =
  | "google_cloud_credits"
  | "elevenlabs_credits"
  | "cash"
  | "cash_unknown_google"
  | "owned_hardware";

/**
 * `owned` is neither cash nor credits: electricity plus depreciation of
 * hardware already bought. It never leaves the bank account per Echo, and no
 * credit grant absorbs it.
 */
export type FundingKind = "cash" | "credits" | "owned";

export type Funding = { key: FundingKey; label: string; kind: FundingKind };

export const FUNDING: Record<FundingKey, Funding> = {
  google_cloud_credits: {
    key: "google_cloud_credits",
    label: "Google Cloud credits",
    kind: "credits",
  },
  elevenlabs_credits: {
    key: "elevenlabs_credits",
    label: "ElevenLabs credits",
    kind: "credits",
  },
  cash: { key: "cash", label: "Cash", kind: "cash" },
  cash_unknown_google: {
    key: "cash_unknown_google",
    label: "Cash (unknown Google channel)",
    kind: "cash",
  },
  owned_hardware: {
    key: "owned_hardware",
    label: "Owned hardware",
    kind: "owned",
  },
};

/**
 * Stable display order: credits first, then cash with its uncertain bucket,
 * owned hardware last.
 */
export const FUNDING_ORDER: FundingKey[] = [
  "google_cloud_credits",
  "elevenlabs_credits",
  "cash",
  "cash_unknown_google",
  "owned_hardware",
];

export function fundingOf(channel: string | null | undefined): Funding {
  switch (channel) {
    case "google_vertex":
      return FUNDING.google_cloud_credits;
    case "elevenlabs":
    case "elevenlabs_grant":
      return FUNDING.elevenlabs_credits;
    case "google_unspecified":
      return FUNDING.cash_unknown_google;
    case "owned_gpu":
      return FUNDING.owned_hardware;
    // `runpod` (rented GPU time), `meta_whatsapp` and `meta_ads` are cash,
    // like every channel not named above.
    default:
      return FUNDING.cash;
  }
}

export type FundingBucket = Funding & { usd: number };

export type FundingSplit = {
  buckets: FundingBucket[];
  cash: number;
  credits: number;
  /** Owned-hardware dollars: neither cash nor credits. */
  ownedHardware: number;
  /** cash + credits + ownedHardware. */
  total: number;
};

/** Sum a channel → USD map into funding buckets (non-finite values ignored). */
export function fundingSplit(
  byChannel: Record<string, number | null | undefined> | null | undefined,
): FundingSplit {
  const totals = new Map<FundingKey, number>();
  for (const [channel, raw] of Object.entries(byChannel ?? {})) {
    const usd = Number(raw);
    if (!Number.isFinite(usd) || usd === 0) continue;
    const funding = fundingOf(channel);
    totals.set(funding.key, (totals.get(funding.key) ?? 0) + usd);
  }
  const buckets = FUNDING_ORDER.filter((key) => totals.has(key)).map((key) => ({
    ...FUNDING[key],
    usd: totals.get(key) ?? 0,
  }));
  const cash = buckets
    .filter((bucket) => bucket.kind === "cash")
    .reduce((sum, bucket) => sum + bucket.usd, 0);
  const credits = buckets
    .filter((bucket) => bucket.kind === "credits")
    .reduce((sum, bucket) => sum + bucket.usd, 0);
  const ownedHardware = buckets
    .filter((bucket) => bucket.kind === "owned")
    .reduce((sum, bucket) => sum + bucket.usd, 0);
  return {
    buckets,
    cash,
    credits,
    ownedHardware,
    total: cash + credits + ownedHardware,
  };
}

/** Channel → USD from a provider → USD map (historical summaries). */
export function channelsFromProviders(
  byProvider: Record<string, number | null | undefined> | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [provider, raw] of Object.entries(byProvider ?? {})) {
    const usd = Number(raw);
    if (!Number.isFinite(usd)) continue;
    const channel = channelForProvider(provider);
    out[channel] = (out[channel] ?? 0) + usd;
  }
  return out;
}

/**
 * Short tag for a table row, e.g. "Vertex AI · Google Cloud credits" or
 * "Owned GPU towers · Owned hardware".
 */
export function channelFundingLabel(channel: string): string {
  return `${channelLabel(channel)} · ${fundingOf(channel).label}`;
}
